import React, { useState, useEffect, useRef } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import {
  ArrowRight,
  ArrowDown,
  ArrowLeft,
  Bed,
  Maximize2,
  Layers,
  Zap,
  Home,
  Clock,
  Heart,
  Shield,
  Factory,
  Truck,
  Forklift,
  Anchor,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { getProduct, products } from "../data/products";
import useSEO from "../hooks/useSEO";

const SITE_URL = "https://yono-habitat.com";

// Stat chips per product (icon + label)
const STATS = {
  "yono-micro": [
    { icon: Bed, label: "1 à 2 pers." },
    { icon: Maximize2, label: "6 m²" },
    { icon: Layers, label: "CLT bois massif" },
    { icon: Zap, label: "Prêt à raccorder" },
  ],
  "yono-studio": [
    { icon: Bed, label: "2 pers." },
    { icon: Maximize2, label: "13 m²" },
    { icon: Layers, label: "CLT 60 mm" },
    { icon: Zap, label: "Prêt à raccorder" },
  ],
  "yono-nido": [
    { icon: Bed, label: "2 à 4 pers." },
    { icon: Maximize2, label: "13 m²" },
    { icon: Layers, label: "CLT 60 mm" },
    { icon: Zap, label: "Prêt à raccorder" },
  ],
  "yono-lodge": [
    { icon: Bed, label: "4 pers." },
    { icon: Maximize2, label: "22 m²" },
    { icon: Layers, label: "CLT bois massif" },
    { icon: Zap, label: "Prêt à raccorder" },
  ],
  "yono-family-lodge": [
    { icon: Bed, label: "6 pers." },
    { icon: Maximize2, label: "32 m² et +" },
    { icon: Layers, label: "2 volumes + véranda" },
    { icon: Zap, label: "Prêt à raccorder" },
  ],
  "yono-twin": [
    { icon: Bed, label: "2 chambres" },
    { icon: Maximize2, label: "13 m²" },
    { icon: Layers, label: "CLT 60 mm" },
    { icon: Zap, label: "Prêt à raccorder" },
  ],
};

// Long subtitle per product (h2 of "Le produit" section)
const LONG_SUBTITLES = {
  "yono-micro": "Six mètres carrés pour densifier un parc et créer une offre insolite",
  "yono-studio": "Un volume compact, une vraie sensation d'espace pour un couple",
  "yono-nido": "Le micro-habitat familial — capacité 2 à 4 personnes sur 13 m²",
  "yono-lodge": "L'alternative bois durable au mobil-home, pour 4 personnes",
  "yono-family-lodge":
    "Deux volumes reliés par une véranda, pensés comme un vrai logement familial",
  "yono-twin":
    "Loger le personnel saisonnier sur site, simplement et dignement",
};

// Advantages (4 cards) per product
const ADVANTAGES = {
  "yono-micro": [
    { icon: Home, t: "Format insolite", d: "Six mètres carrés autonomes pour valoriser un petit emplacement et créer une offre différenciante." },
    { icon: Clock, t: "Installation rapide", d: "Module préfabriqué, livré et installé en quelques heures sur emplacement existant." },
    { icon: Heart, t: "Rentabilité au m²", d: "Le format le plus optimisé de la gamme pour maximiser la rentabilité du parc." },
    { icon: Shield, t: "Qualité CLT", d: "La même exigence constructive que l'ensemble de la gamme YONO, en bois massif." },
  ],
  "yono-studio": [
    { icon: Home, t: "Format ouvert", d: "Espace de vie généreux en rez-de-chaussée avec banquette, kitchenette et salle d'eau." },
    { icon: Clock, t: "Installation rapide", d: "Un seul habitat transportable, à poser et à raccorder en quelques heures." },
    { icon: Heart, t: "Confort mezzanine", d: "Un espace couchage en hauteur qui libère le volume de vie au sol." },
    { icon: Shield, t: "Qualité CLT", d: "La même exigence constructive que l'ensemble de la gamme YONO, en bois massif." },
  ],
  "yono-nido": [
    { icon: Home, t: "Format familial", d: "Une chambre indépendante en rez-de-chaussée et des couchages complémentaires pour 4 personnes." },
    { icon: Clock, t: "Capacité optimisée", d: "Jusqu'à 4 personnes sur 13 m² — maximise la capacité d'accueil au m²." },
    { icon: Heart, t: "Confort compact", d: "Un volume optimisé qui intègre tout le nécessaire, sans renoncer au confort." },
    { icon: Shield, t: "Qualité CLT", d: "La même exigence constructive que l'ensemble de la gamme YONO, en bois massif." },
  ],
  "yono-lodge": [
    { icon: Home, t: "Logement 4 personnes", d: "Vingt-deux mètres carrés pour quatre personnes, séjour, espaces nuit et cuisine équipée." },
    { icon: Clock, t: "Alternative mobil-home", d: "Une réponse plus architecturale et durable que le mobil-home traditionnel." },
    { icon: Heart, t: "Confort durable", d: "Structure bois massif et finitions qualitatives pour un usage régulier et intensif." },
    { icon: Shield, t: "Qualité CLT", d: "La même exigence constructive que l'ensemble de la gamme YONO, en bois massif." },
  ],
  "yono-family-lodge": [
    { icon: Home, t: "Grand format familial", d: "Trente mètres carrés et plus, jusqu'à 6 personnes, plusieurs espaces nuit et grand séjour." },
    { icon: Clock, t: "Séjour longue durée", d: "Pensé pour les familles et les séjours longs : vrai logement de vacances en bois." },
    { icon: Heart, t: "Grande luminosité", d: "Véranda centrale qui apporte de la lumière naturelle au cœur du logement." },
    { icon: Shield, t: "Qualité CLT", d: "La même exigence constructive que l'ensemble de la gamme YONO, en bois massif." },
  ],
  "yono-twin": [
    { icon: Home, t: "Deux chambres", d: "Deux chambres indépendantes et une salle d'eau centrale partagée." },
    { icon: Clock, t: "Idéal saisonniers", d: "Format dédié au logement du personnel saisonnier, simple, sobre et efficace." },
    { icon: Heart, t: "Installation rapide", d: "Module préfabriqué, prêt à raccorder, à implanter directement sur site." },
    { icon: Shield, t: "Qualité CLT", d: "La même exigence constructive que l'ensemble de la gamme YONO, en bois massif." },
  ],
};

const MISE_EN_OEUVRE = [
  { icon: Factory, t: "Fabrication en atelier", d: "Préfabrication complète en environnement contrôlé pour une qualité constante." },
  { icon: Truck, t: "Livré prêt à raccorder", d: "Modules transportés par camion et installés directement sur site." },
  { icon: Forklift, t: "Passage de fourche", d: "Structure dimensionnée pour la manutention par chariot élévateur." },
  { icon: Anchor, t: "Pose sur pieds métalliques", d: "Installation sans fondation lourde, adaptable au terrain." },
];

function buildJsonLd(product) {
  if (!product) return null;
  const fullName = `YONO ${product.name}`;
  // Prix indicatif SEO uniquement (jamais affiché aux visiteurs).
  // Requis par Google pour la validation du structured data Product/Offer
  // et les rich snippets. Le vrai devis reste "sur devis" côté UI.
  const seoPrice = product.priceHT || 15000;
  const rawImage = product.hero || product.cardImage;
  const absoluteImage = rawImage && !/^https?:\/\//i.test(rawImage)
    ? `${SITE_URL}${rawImage.startsWith("/") ? rawImage : `/${rawImage}`}`
    : rawImage;
  const ld = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: fullName,
    description: product.intro,
    image: absoluteImage,
    brand: { "@type": "Brand", name: "YONO Habitat" },
    category: "Habitat modulaire bois",
    sku: product.slug,
    mpn: product.slug.toUpperCase(),
    offers: {
      "@type": "Offer",
      availability: "https://schema.org/InStock",
      price: String(seoPrice),
      priceCurrency: "EUR",
      priceValidUntil: `${new Date().getFullYear() + 1}-12-31`,
      url: `${SITE_URL}/produits/${product.slug}`,
      seller: { "@type": "Organization", name: "YONO Habitat" },
    },
  };
  return ld;
}

// ─── HERO ──────────────────────────────────────────────────────────────────
function ProductHero({ product, stats }) {
  const bg = product.hero || product.cardImage;
  return (
    <section
      className="relative w-full bg-[var(--yono-off-white)] border-b border-[var(--yono-border)]"
      data-testid="product-hero"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12">
        {/* Text panel (light) */}
        <div className="lg:col-span-5 order-2 lg:order-1 bg-[var(--yono-off-white)]">
          <div className="px-6 sm:px-10 lg:px-14 py-12 lg:py-20 max-w-2xl">
            <div className="eyebrow text-[var(--yono-wood)] mb-5 fade-up">
              YONO · {product.surface} · {product.capacity}
            </div>
            <h1 className="font-heading text-[28px] sm:text-[36px] lg:text-[44px] leading-[1.05] tracking-tight text-[var(--yono-soft-black)] fade-up">
              {product.name}
            </h1>
            <p className="mt-4 text-[15px] sm:text-[16px] text-[var(--yono-warm-grey)] leading-[1.55]">
              {product.tagline}
            </p>
            <p className="mt-6 text-[14px] sm:text-[15px] leading-[1.85] text-[var(--yono-soft-black)]/80">
              {product.intro}
            </p>

            <div className="mt-8 flex flex-wrap gap-x-5 gap-y-3">
              {stats?.map(({ icon: Icon, label }) => (
                <div
                  key={label}
                  className="inline-flex items-center gap-2 text-[11px] tracking-[0.15em] uppercase text-[var(--yono-warm-grey)]"
                >
                  <Icon
                    size={14}
                    strokeWidth={1.4}
                    className="text-[var(--yono-wood)]"
                  />
                  {label}
                </div>
              ))}
            </div>

            <div className="mt-9 flex flex-wrap gap-3">
              <Link to="/contact" className="btn-primary" data-testid="hero-cta-contact">
                Étudier mon projet <ArrowRight size={14} strokeWidth={1.5} />
              </Link>
              <a href="#produit-detail" className="btn-outline">
                Découvrir le produit <ArrowDown size={14} strokeWidth={1.5} />
              </a>
            </div>
          </div>
        </div>

        {/* Image panel */}
        <div className="lg:col-span-7 order-1 lg:order-2 relative bg-[var(--yono-soft-black)]">
          <div
            className="relative w-full overflow-hidden"
            style={{ aspectRatio: "4 / 3" }}
          >
            {bg ? (
              <img
                src={bg}
                alt={`${product.name} — habitat bois modulaire YONO ${product.surface}`}
                className={`absolute inset-0 w-full h-full ${
                  product.cardFit === "contain" ? "object-contain" : "object-cover"
                }`}
              />
            ) : (
              <div className="absolute inset-0 placeholder-bois" />
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── LE PRODUIT ────────────────────────────────────────────────────────────
function LeProduit({ product, longSubtitle }) {
  const renderImg = product.gallery?.[1] || product.gallery?.[0] || product.hero;
  return (
    <section className="section" id="produit-detail">
      <div className="container-yono">
        <p className="eyebrow mb-6">Le produit</p>
        <h2 className="font-heading text-[26px] sm:text-[30px] lg:text-[36px] tracking-tight leading-[1.18] max-w-3xl">
          {longSubtitle}
        </h2>
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          <p className="text-[15px] leading-[1.85] text-[var(--yono-soft-black)]/85">
            {product.intro}
          </p>
          <p className="text-[15px] leading-[1.85] text-[var(--yono-soft-black)]/85">
            {product.positioning}
          </p>
        </div>
        {renderImg && (
          <div className="mt-12 aspect-[16/9] overflow-hidden border border-[var(--yono-border)] bg-[var(--yono-beige)] flex items-center justify-center">
            <img src={renderImg} alt={product.name} className="w-full h-full object-contain" />
          </div>
        )}
      </div>
    </section>
  );
}

// ─── AVANTAGES (4 cards on beige) ──────────────────────────────────────────
function Avantages({ items }) {
  return (
    <section className="section bg-[var(--yono-beige)]">
      <div className="container-yono">
        <p className="eyebrow mb-6">Avantages</p>
        <h2 className="font-heading text-[26px] sm:text-[30px] lg:text-[36px] tracking-tight leading-[1.18] max-w-3xl">
          Points forts
        </h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--yono-border)] border border-[var(--yono-border)]">
          {items.map(({ icon: Icon, t, d }) => (
            <div key={t} className="bg-[var(--yono-beige)] p-8 lg:p-10">
              <Icon size={26} strokeWidth={1.3} className="text-[var(--yono-wood)] mb-6" />
              <h3 className="font-heading text-lg mb-3">{t}</h3>
              <p className="text-[14px] leading-relaxed text-[var(--yono-warm-grey)]">{d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── FICHE TECHNIQUE ───────────────────────────────────────────────────────
function TechSheet({ product }) {
  const { tech } = product;
  if (!tech) return null;

  const techEntries = Object.entries(tech);

  return (
    <section className="section">
      <div className="container-yono">
        <p className="eyebrow mb-6">Fiche technique</p>
        <h2 className="font-heading text-[26px] sm:text-[30px] lg:text-[36px] tracking-tight leading-[1.18] max-w-3xl mb-12">
          Caractéristiques techniques
        </h2>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-12">
          {techEntries.map(([k, v]) => (
            <div
              key={k}
              className="flex justify-between gap-4 py-3 border-b border-[var(--yono-border)]"
            >
              <dt className="text-[12px] tracking-[0.1em] uppercase text-[var(--yono-warm-grey)]">
                {k}
              </dt>
              <dd className="text-[14px] text-right max-w-[60%]">{v}</dd>
            </div>
          ))}
        </dl>

        {/* Bloc tarification sur devis */}
        <div
          className="mt-12 border border-[var(--yono-border)] bg-[var(--yono-beige)] p-8 lg:p-10"
          data-testid="tarif-sur-devis-block"
        >
          <p className="eyebrow text-[var(--yono-wood)] mb-3">Tarification</p>
          <p className="font-heading text-[22px] sm:text-[26px] tracking-tight leading-[1.2] text-[var(--yono-soft-black)] max-w-2xl">
            Tarif sur devis, selon la configuration, les équipements, le
            volume de commande et le lieu de livraison.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link
              to="/contact"
              className="btn-primary"
              data-testid="tech-cta-devis"
            >
              Demander un devis <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
          </div>
          <p className="mt-5 text-[14px] leading-[1.65] text-[var(--yono-warm-grey)] max-w-2xl">
            Décrivez-nous votre projet : nombre de logements, typologies,
            options souhaitées et localisation. Notre équipe vous transmettra
            une proposition détaillée.
          </p>
        </div>
      </div>
    </section>
  );
}

// ─── MISE EN OEUVRE ────────────────────────────────────────────────────────
function MiseEnOeuvre() {
  return (
    <section className="section bg-[var(--yono-beige)]">
      <div className="container-yono">
        <p className="eyebrow mb-6">Mise en œuvre</p>
        <h2 className="font-heading text-[26px] sm:text-[30px] lg:text-[36px] tracking-tight leading-[1.18] max-w-3xl">
          Simple à mettre en œuvre
        </h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--yono-border)] border border-[var(--yono-border)]">
          {MISE_EN_OEUVRE.map(({ icon: Icon, t, d }) => (
            <div key={t} className="bg-[var(--yono-beige)] p-8 lg:p-10">
              <Icon size={26} strokeWidth={1.3} className="text-[var(--yono-wood)] mb-6" />
              <h3 className="font-heading text-lg mb-3">{t}</h3>
              <p className="text-[14px] leading-relaxed text-[var(--yono-warm-grey)]">{d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── GALERIE (carrousel) ───────────────────────────────────────────────────
function Galerie({ product }) {
  const images = product.gallery && product.gallery.length > 0
    ? product.gallery
    : (product.hero ? [product.hero] : []);
  const [active, setActive] = useState(0);
  const trackRef = useRef(null);

  useEffect(() => {
    setActive(0);
  }, [product.slug]);

  if (images.length === 0) return null;

  const goPrev = () => setActive((i) => (i - 1 + images.length) % images.length);
  const goNext = () => setActive((i) => (i + 1) % images.length);

  return (
    <section className="section">
      <div className="container-yono">
        <p className="eyebrow mb-6">Visuels</p>
        <h2 className="font-heading text-[26px] sm:text-[30px] lg:text-[36px] tracking-tight leading-[1.18] max-w-3xl">
          Galerie
        </h2>

        <div className="mt-10 relative">
          <div
            ref={trackRef}
            className="relative aspect-[16/10] overflow-hidden border border-[var(--yono-border)] product-card-img"
          >
            {images.map((src, i) => (
              <img
                key={src}
                src={src}
                alt={`${product.name} — visuel ${i + 1}`}
                className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500"
                style={{ opacity: i === active ? 1 : 0 }}
              />
            ))}
          </div>

          {images.length > 1 && (
            <>
              <button
                onClick={goPrev}
                aria-label="Image précédente"
                data-testid="gallery-prev"
                className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white border border-[var(--yono-border)] p-3"
              >
                <ChevronLeft size={20} strokeWidth={1.4} />
              </button>
              <button
                onClick={goNext}
                aria-label="Image suivante"
                data-testid="gallery-next"
                className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white border border-[var(--yono-border)] p-3"
              >
                <ChevronRight size={20} strokeWidth={1.4} />
              </button>

              <div className="mt-4 grid grid-cols-6 gap-2">
                {images.map((src, i) => (
                  <button
                    key={src}
                    onClick={() => setActive(i)}
                    data-testid={`gallery-thumb-${i}`}
                    className={`aspect-square overflow-hidden product-card-img transition-opacity ${
                      i === active ? "opacity-100" : "opacity-50 hover:opacity-100"
                    }`}
                  >
                    <img src={src} alt={`${product.name} miniature ${i + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

// ─── CTA FINAL (background image) ─────────────────────────────────────────
function FinalCTA({ product }) {
  const bg = product.gallery?.[0] || product.hero || product.cardImage;
  return (
    <section
      className="relative w-full overflow-hidden mt-12 flex items-center justify-center"
      style={{ minHeight: 420 }}
    >
      {bg && (
        <img src={bg} alt="" className="absolute inset-0 w-full h-full object-cover" />
      )}
      <div className="absolute inset-0 bg-black/65" />
      <div className="relative container-yono py-20 text-white text-center w-full">
        <h3 className="font-heading text-[26px] sm:text-[30px] lg:text-[36px] tracking-tight max-w-3xl mx-auto leading-[1.15]">
          Envie d'un habitat bois compact, simple et prêt à exploiter&nbsp;?
        </h3>
        <p className="mt-4 text-white/85 max-w-2xl mx-auto text-[15px]">
          Échangeons sur votre site, vos contraintes d'exploitation et les
          configurations possibles du {product.name}.
        </p>
        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          <Link to="/contact" className="btn-primary !bg-white !text-[var(--yono-soft-black)] !border-white">
            Étudier mon projet <ArrowRight size={14} strokeWidth={1.5} />
          </Link>
          <Link to="/gamme" className="btn-outline-light">
            Voir toute la gamme
          </Link>
        </div>
      </div>
    </section>
  );
}

// ─── MAIN ──────────────────────────────────────────────────────────────────
// ─── PLAN VUE DE DESSUS ────────────────────────────────────────────────────
function PlanSection({ product }) {
  return (
    <section className="section bg-[var(--yono-beige)]">
      <div className="container-yono">
        <p className="eyebrow mb-6">Plan</p>
        <h2 className="font-heading text-[26px] sm:text-[30px] lg:text-[36px] tracking-tight leading-[1.18] max-w-3xl mb-10">
          Aménagement intérieur — vue de dessus
        </h2>
        <div className="flex items-center justify-center">
          <img
            src={product.planImage}
            alt={`Plan d'aménagement ${product.name}`}
            className={`w-full h-auto ${product.planLarge ? "max-w-7xl" : "max-w-4xl"} object-contain`}
            data-testid={`plan-image-${product.slug}`}
          />
        </div>
        <p className="mt-6 text-[12px] tracking-[0.05em] text-[var(--yono-warm-grey)] italic">
          Plan indicatif — l'agencement peut varier selon configuration et options.
        </p>
      </div>
    </section>
  );
}

export default function Produit() {
  const { slug } = useParams();
  const product = getProduct(slug);

  useSEO({
    title: product ? (product.seoTitle || `${product.name} — ${product.tagline}`) : "Produit",
    description: product
      ? `${product.name} : ${product.intro.substring(0, 160)}`
      : "Habitat bois YONO",
    path: product ? `/produits/${product.slug}` : "/produits",
    ogImage: product?.hero || product?.cardImage,
    jsonLd: buildJsonLd(product),
  });

  if (!product) return <Navigate to="/produits" replace />;

  const stats = STATS[product.slug] || [];
  const longSubtitle = LONG_SUBTITLES[product.slug] || product.tagline;
  const advantages = ADVANTAGES[product.slug] || [];

  return (
    <div data-testid={`product-page-${product.slug}`}>
      <div className="container-yono pt-6">
        <Link
          to="/gamme"
          className="inline-flex items-center gap-2 text-[11px] tracking-[0.15em] uppercase text-[var(--yono-warm-grey)] hover:text-[var(--yono-soft-black)]"
          data-testid="back-to-gamme-btn"
        >
          <ArrowLeft size={13} strokeWidth={1.5} /> Retour à la gamme
        </Link>
      </div>

      <ProductHero product={product} stats={stats} />
      <LeProduit product={product} longSubtitle={longSubtitle} />
      {advantages.length > 0 && <Avantages items={advantages} />}
      {product.planImage && <PlanSection product={product} />}
      <MiseEnOeuvre />
      <TechSheet product={product} />
      <Galerie product={product} />
      <FinalCTA product={product} />
    </div>
  );
}
